import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FileDown, LogOut, Check, X, MessageSquare, Loader2, PackageSearch, Truck, Receipt, CreditCard, Repeat, Paperclip, Bell, ChevronDown, ChevronUp, Inbox, Search, FileText, ShoppingCart, PackageCheck } from "lucide-react";
import RecurringTab from "./RecurringTab";
import { FederalJourney, FederalInfo } from "./FederalTracker";
import api from "../lib/api";
import { useAuth } from "../admin/AuthContext";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ORDER_STEPS = ["ORDERED", "SUPPLIER_CONFIRMED", "IN_PRODUCTION", "READY_TO_SHIP", "IN_TRANSIT", "OUT_FOR_DELIVERY", "DELIVERED"];

const RFQ_STATUS_LABEL = {
  NEW: ["Received", "bg-blue-100 text-blue-700"],
  REVIEW: ["Under Review", "bg-indigo-100 text-indigo-700"],
  SOURCING: ["Sourcing Suppliers", "bg-amber-100 text-amber-700"],
  SUPPLIER_QUOTES_PENDING: ["Sourcing Suppliers", "bg-amber-100 text-amber-700"],
  ANALYSIS: ["Preparing Your Quote", "bg-amber-100 text-amber-700"],
  CUSTOMER_QUOTE: ["Quote In Progress", "bg-cyan-100 text-cyan-700"],
  NEGOTIATION: ["In Discussion", "bg-purple-100 text-purple-700"],
  WON: ["Order Created", "bg-green-100 text-green-700"],
  LOST: ["Closed", "bg-slate-200 text-slate-500"],
  PURCHASE_ORDER: ["Order Created", "bg-green-100 text-green-700"],
  FULFILLMENT: ["In Fulfillment", "bg-green-100 text-green-700"],
  SHIPPING: ["Shipping", "bg-green-100 text-green-700"],
  DELIVERED: ["Delivered", "bg-green-100 text-green-700"],
  CLOSED: ["Completed", "bg-slate-200 text-slate-500"],
};

const JOURNEY_META = {
  RECEIVED: ["Received", Inbox, "Your RFQ was received by our procurement desk."],
  SOURCING: ["Sourcing", Search, "Our team is sourcing qualified suppliers for your requirement."],
  QUOTED: ["Quoted", FileText, "Your quote has been prepared and sent for review."],
  ORDERED: ["Ordered", ShoppingCart, "Order created — your purchase is in fulfillment."],
  DELIVERED: ["Delivered", PackageCheck, "Your order has been delivered."],
};

function RfqJourney({ rfq }) {
  const [expanded, setExpanded] = useState(false);
  const j = rfq.journey;
  if (!j) return null;
  const stages = j.stages || [];
  if (j.closed) {
    return (
      <div className="mt-4 flex items-center gap-2 text-[12.5px] text-slate-400" data-testid={`rfq-journey-closed-${rfq.rfq_number}`}>
        <X size={13} /> This request was closed. Submit a new RFQ anytime — we're ready when you are.
      </div>
    );
  }
  return (
    <div className="mt-4 border-t border-slate-100 pt-4">
      <div className="flex items-start" data-testid={`rfq-progress-${rfq.rfq_number}`}>
        {stages.map((s, i) => {
          const [label, Icon] = JOURNEY_META[s.key];
          const done = i < j.current;
          const active = i === j.current;
          return (
            <div key={s.key} className="flex-1 flex flex-col items-center relative">
              {i > 0 && <div className={`absolute top-[12px] right-1/2 w-full h-[2.5px] ${i <= j.current ? "bg-[var(--pg-accent)]" : "bg-slate-200"}`} style={{ zIndex: 0 }} />}
              <div className={`relative z-10 w-[25px] h-[25px] rounded-full flex items-center justify-center ${done ? "bg-[var(--pg-accent)] text-white" : active ? "bg-[var(--pg-navy)] text-white ring-4 ring-[var(--pg-navy)]/15" : "bg-slate-200 text-slate-400"}`}>
                {done ? <Check size={13} /> : <Icon size={12} />}
              </div>
              <span className={`mt-1.5 text-[10px] font-[Poppins] font-semibold uppercase tracking-wide ${done || active ? "text-[var(--pg-navy)]" : "text-slate-400"}`}>{label}</span>
            </div>
          );
        })}
      </div>
      <button data-testid={`rfq-timeline-toggle-${rfq.rfq_number}`} onClick={() => setExpanded(!expanded)}
        className="mt-3 inline-flex items-center gap-1 text-[12px] font-[Poppins] font-semibold text-slate-500 hover:text-[var(--pg-navy)] transition-colors">
        {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />} {expanded ? "Hide timeline" : "View full timeline"}
      </button>
      {expanded && (
        <div className="mt-3 space-y-0" data-testid={`rfq-timeline-${rfq.rfq_number}`}>
          {stages.map((s, i) => {
            const [label, Icon, desc] = JOURNEY_META[s.key];
            const done = i < j.current;
            const active = i === j.current;
            const reached = done || active;
            return (
              <div key={s.key} className="flex gap-3 relative pb-5 last:pb-0">
                {i < stages.length - 1 && <div className={`absolute left-[11px] top-[24px] bottom-0 w-[2px] ${done ? "bg-[var(--pg-accent)]" : "bg-slate-200"}`} />}
                <div className={`relative z-10 w-[23px] h-[23px] rounded-full flex-shrink-0 flex items-center justify-center ${done ? "bg-[var(--pg-accent)] text-white" : active ? "bg-[var(--pg-navy)] text-white" : "bg-slate-100 text-slate-400"}`}>
                  {done ? <Check size={12} /> : <Icon size={11} />}
                </div>
                <div>
                  <div className={`text-[13px] font-[Poppins] font-semibold ${reached ? "text-[var(--pg-navy)]" : "text-slate-400"}`}>
                    {label}
                    {active && <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] bg-[var(--pg-navy)] text-white uppercase tracking-wide">Current</span>}
                    {s.at && <span className="ml-2 text-[11px] font-normal text-slate-400">{s.at.slice(0, 10)}</span>}
                  </div>
                  <p className={`text-[12px] mt-0.5 ${reached ? "text-slate-500" : "text-slate-300"}`}>{desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const QUOTE_BADGE = {
  SENT: "bg-blue-100 text-blue-700", ACCEPTED: "bg-green-100 text-green-700",
  DECLINED: "bg-red-100 text-red-600", EXPIRED: "bg-slate-200 text-slate-500",
};

export default function Portal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("quotes");
  const [rfqs, setRfqs] = useState(null);
  const [orders, setOrders] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [needsVerify, setNeedsVerify] = useState(false);

  const load = useCallback(() => {
    api.get("/portal/rfqs").then((r) => setRfqs(r.data)).catch((err) => {
      setRfqs([]);
      if (err?.response?.status === 403) setNeedsVerify(true);
    });
    api.get("/portal/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));
    api.get("/portal/invoices").then((r) => setInvoices(r.data)).catch(() => setInvoices([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (needsVerify) {
    return (
      <div data-testid="portal-needs-verify" className="min-h-screen bg-[var(--pg-bg)] pt-[140px] pb-20">
        <div className="pg-container max-w-lg text-center bg-white border border-slate-200 rounded-2xl p-10">
          <h1 className="font-[Poppins] font-bold text-[22px] text-[var(--pg-navy)]">Please verify your email</h1>
          <p className="mt-3 text-[14.5px] text-slate-500">We sent a verification link to <strong>{user?.email}</strong>. Click it to unlock your RFQs, quotes, and orders.</p>
          <button data-testid="portal-verify-signout" onClick={async () => { await logout(); navigate("/portal/login"); }} className="btn-primary mt-6">Back to Sign In</button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="portal-page" className="min-h-screen bg-[var(--pg-bg)]">
      <section className="pt-[120px] pb-10 hero-bg">
        <div className="pg-container flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="eyebrow" style={{ color: "#7EC4FF" }}>Client Portal</span>
            <h1 className="mt-3 text-[30px] md:text-[40px] font-extrabold leading-[1.06] text-white">
              Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
            </h1>
            <p className="mt-2 text-white/75 text-[15px]">Track your RFQs, respond to quotes, and follow your orders to delivery.</p>
          </div>
          <div className="flex items-center gap-3">
            <NotificationsBell setTab={setTab} />
            <button data-testid="portal-signout-button" onClick={async () => { await logout(); navigate("/"); }}
              className="inline-flex items-center gap-2 text-[13px] font-[Poppins] font-semibold text-white/80 hover:text-white border border-white/30 rounded-full px-4 py-2.5 transition-colors">
              <LogOut size={14} /> Sign Out
            </button>
          </div>
        </div>
      </section>

      <div className="pg-container py-10">
        <div className="flex gap-2 mb-8">
          <button data-testid="portal-tab-quotes" onClick={() => setTab("quotes")}
            className={`inline-flex items-center gap-2 px-5 py-3 rounded-full font-[Poppins] font-semibold text-[13.5px] transition-colors ${tab === "quotes" ? "bg-[var(--pg-navy)] text-white" : "bg-white border border-slate-200 text-slate-500 hover:text-[var(--pg-navy)]"}`}>
            <PackageSearch size={15} /> My RFQs &amp; Quotes
          </button>
          <button data-testid="portal-tab-orders" onClick={() => setTab("orders")}
            className={`inline-flex items-center gap-2 px-5 py-3 rounded-full font-[Poppins] font-semibold text-[13.5px] transition-colors ${tab === "orders" ? "bg-[var(--pg-navy)] text-white" : "bg-white border border-slate-200 text-slate-500 hover:text-[var(--pg-navy)]"}`}>
            <Truck size={15} /> My Orders {orders?.length ? `(${orders.length})` : ""}
          </button>
          <button data-testid="portal-tab-invoices" onClick={() => setTab("invoices")}
            className={`inline-flex items-center gap-2 px-5 py-3 rounded-full font-[Poppins] font-semibold text-[13.5px] transition-colors ${tab === "invoices" ? "bg-[var(--pg-navy)] text-white" : "bg-white border border-slate-200 text-slate-500 hover:text-[var(--pg-navy)]"}`}>
            <Receipt size={15} /> My Invoices {invoices?.length ? `(${invoices.length})` : ""}
          </button>
          <button data-testid="portal-tab-recurring" onClick={() => setTab("recurring")}
            className={`inline-flex items-center gap-2 px-5 py-3 rounded-full font-[Poppins] font-semibold text-[13.5px] transition-colors ${tab === "recurring" ? "bg-[var(--pg-navy)] text-white" : "bg-white border border-slate-200 text-slate-500 hover:text-[var(--pg-navy)]"}`}>
            <Repeat size={15} /> Recurring
          </button>
        </div>

        {tab === "quotes" && <QuotesTab rfqs={rfqs} onChange={load} />}
        {tab === "orders" && <OrdersTab orders={orders} onChange={load} setTab={setTab} />}
        {tab === "invoices" && <InvoicesTab invoices={invoices} />}
        {tab === "recurring" && <RecurringTab />}
      </div>
    </div>
  );
}

function QuotesTab({ rfqs, onChange }) {
  if (rfqs === null) return <div className="text-slate-400 py-10" data-testid="portal-loading">Loading your RFQs...</div>;
  if (rfqs.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center" data-testid="portal-rfqs-empty">
        <p className="text-[15px] text-slate-500">No RFQs found for your email address yet.</p>
        <Link to="/request-a-quote" data-testid="portal-new-rfq-link" className="btn-primary mt-5 inline-flex">Submit Your First Procurement Request</Link>
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="portal-rfq-list">
      {rfqs.map((r) => {
        const [label, cls] = RFQ_STATUS_LABEL[r.status] || ["Received", "bg-blue-100 text-blue-700"];
        return (
        <div key={r.id} className="bg-white rounded-2xl border border-slate-200 p-6" data-testid={`portal-rfq-${r.rfq_number}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2.5 flex-wrap">
              <span className="font-[Poppins] font-bold text-[16px] text-[var(--pg-navy)]">{r.rfq_number}</span>
              <span data-testid={`portal-rfq-status-${r.rfq_number}`} className={`px-2.5 py-1 rounded-full text-[11px] font-[Poppins] font-semibold ${cls}`}>{label}</span>
              {r.requirement_type && r.requirement_type !== "Commercial Procurement" && (
                <span data-testid={`portal-rfq-type-${r.rfq_number}`} className="px-2.5 py-1 rounded-full text-[11px] font-[Poppins] font-semibold bg-[#e6f1fb] text-[var(--pg-navy)]">{r.requirement_type}</span>
              )}
              <span className="text-[13.5px] text-slate-500">{r.product_service}</span>
            </div>
            <span className="text-[12px] text-slate-400">Submitted {(r.created_at || "").slice(0, 10)}</span>
          </div>
          {(r.documents || []).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2" data-testid={`portal-rfq-docs-${r.rfq_number}`}>
              {r.documents.map((d) => <DocChip key={d.id} doc={d} />)}
            </div>
          )}
          <FederalInfo rfq={r} />
          {r.federal_journey ? <FederalJourney rfq={r} /> : <RfqJourney rfq={r} />}
          {(r.quotes || []).length === 0 ? (
            <p className="mt-3 text-[13.5px] text-slate-400">Our team is reviewing this request. Quotes will appear here.</p>
          ) : (
            <div className="mt-4 space-y-3">
              {r.quotes.map((q) => <QuoteCard key={q.id} quote={q} onChange={onChange} />)}
            </div>
          )}
        </div>
        );
      })}
    </div>
  );
}

function DocChip({ doc }) {
  const download = async () => {
    try {
      const res = await api.get(`/portal/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = doc.original_filename || "document"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Document download failed"); }
  };
  return (
    <button data-testid={`portal-doc-${doc.id}`} onClick={download}
      className="inline-flex items-center gap-1.5 text-[12px] font-medium text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 hover:border-[var(--pg-accent)] hover:text-[var(--pg-navy)]">
      <Paperclip size={12} /> {doc.original_filename}
    </button>
  );
}

const viewedQuotes = new Set();

function QuoteCard({ quote, onChange }) {
  const [action, setAction] = useState(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (quote.status === "SENT" && !viewedQuotes.has(quote.id)) {
      viewedQuotes.add(quote.id);
      api.post(`/portal/quotes/${quote.id}/viewed`).catch(() => {});
    }
  }, [quote.id, quote.status]);

  const respond = async (act) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/portal/quotes/${quote.id}/respond`, { action: act, message });
      toast.success(data.message);
      setAction(null); setMessage("");
      onChange();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Action failed. Please try again.");
    } finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/portal/quotes/${quote.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${quote.quote_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF download failed"); }
  };

  return (
    <div className="border border-slate-200 rounded-xl p-5 bg-[var(--pg-bg)]" data-testid={`portal-quote-${quote.quote_number}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2.5">
            <span className="font-[Poppins] font-bold text-[15px] text-[var(--pg-navy)]">{quote.quote_number}</span>
            <span className={`px-2.5 py-1 rounded-full text-[11px] font-[Poppins] font-semibold ${QUOTE_BADGE[quote.status] || "bg-slate-100 text-slate-600"}`}>{quote.status}</span>
          </div>
          <div className="mt-1.5 text-[13px] text-slate-500">
            {quote.product} · Qty {quote.quantity} · <strong className="text-[var(--pg-navy)]">{money(quote.total)}</strong>
            {quote.expires_on && quote.status === "SENT" && <span className="ml-2 text-amber-600 font-medium">Valid until {quote.expires_on}</span>}
          </div>
          {quote.status === "ACCEPTED" && quote.accepted_at && (
            <div data-testid={`portal-quote-accepted-info-${quote.id}`} className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-medium text-green-800 bg-green-50 border border-green-200 rounded-md px-2.5 py-1">
              <Check size={12} /> Accepted on {quote.accepted_at.slice(0, 10)}{quote.accepted_by ? ` by ${quote.accepted_by}` : ""}
            </div>
          )}
          {quote.status === "SENT" && quote.changes_requested_at && (
            <div data-testid={`portal-quote-changes-info-${quote.id}`} className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1">
              <MessageSquare size={12} /> Changes requested on {quote.changes_requested_at.slice(0, 10)} — a Patrick Global specialist will follow up
            </div>
          )}
          {quote.status === "DECLINED" && quote.declined_at && (
            <div data-testid={`portal-quote-declined-info-${quote.id}`} className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-medium text-red-700 bg-red-50 border border-red-200 rounded-md px-2.5 py-1">
              <X size={12} /> Declined on {quote.declined_at.slice(0, 10)}{quote.declined_reason ? ` — "${quote.declined_reason}"` : ""}
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid={`portal-quote-pdf-${quote.id}`} onClick={downloadPdf}
            className="inline-flex items-center gap-1.5 text-[12.5px] font-[Poppins] font-semibold px-3.5 py-2 rounded-lg bg-white border border-slate-200 hover:border-[var(--pg-accent)]"><FileDown size={14} /> PDF</button>
          {quote.status === "SENT" && (
            <>
              <button data-testid={`portal-quote-accept-${quote.id}`} onClick={() => setAction(action === "accept" ? null : "accept")}
                className="inline-flex items-center gap-1.5 text-[12.5px] font-[Poppins] font-semibold px-3.5 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700"><Check size={14} /> Accept Quote</button>
              {!quote.changes_requested_at && (
                <button data-testid={`portal-quote-changes-${quote.id}`} onClick={() => setAction(action === "request_changes" ? null : "request_changes")}
                  className="inline-flex items-center gap-1.5 text-[12.5px] font-[Poppins] font-semibold px-3.5 py-2 rounded-lg bg-[var(--pg-navy)] text-white hover:bg-[var(--pg-accent)]"><MessageSquare size={14} /> Request Changes</button>
              )}
              <button data-testid={`portal-quote-decline-${quote.id}`} onClick={() => setAction(action === "decline" ? null : "decline")}
                className="inline-flex items-center gap-1.5 text-[12.5px] font-[Poppins] font-semibold px-3.5 py-2 rounded-lg bg-white border border-red-200 text-red-600 hover:bg-red-50"><X size={14} /> Decline</button>
            </>
          )}
        </div>
      </div>
      {action && (
        <div className="mt-4 bg-white border border-slate-200 rounded-lg p-4" data-testid="portal-quote-confirm-panel">
          <p className="text-[13.5px] font-[Poppins] font-semibold text-[var(--pg-navy)]">
            {action === "accept" && `Confirm acceptance of ${quote.quote_number} for ${money(quote.total)}? Your order will be created immediately.`}
            {action === "decline" && `Decline ${quote.quote_number}? You can tell us why below (optional).`}
            {action === "request_changes" && "What would you like us to change? (quantity, price, lead time, etc.)"}
          </p>
          {(action === "decline" || action === "request_changes") && (
            <textarea data-testid="portal-quote-message-input" rows={2} className="pg-input mt-3 !py-2.5 text-[13.5px]" placeholder="Your message..."
              value={message} onChange={(e) => setMessage(e.target.value)} />
          )}
          <div className="mt-3 flex gap-2">
            <button data-testid="portal-quote-confirm-button" disabled={busy} onClick={() => respond(action)}
              className="btn-primary !py-2.5 !px-5 text-[13px] disabled:opacity-70">
              {busy ? <Loader2 className="animate-spin" size={15} /> : "Confirm"}
            </button>
            <button data-testid="portal-quote-cancel-button" onClick={() => setAction(null)} className="text-[13px] font-[Poppins] font-semibold text-slate-500 px-4">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function OrdersTab({ orders, onChange, setTab }) {
  const [busyId, setBusyId] = useState(null);
  const [reorderId, setReorderId] = useState(null);
  const [qty, setQty] = useState("");
  const openReorder = (o) => { setReorderId(o.id); setQty(String(o.quantity || "")); };
  const reorder = async (o) => {
    setBusyId(o.id);
    try {
      const { data } = await api.post(`/portal/orders/${o.id}/reorder`, { quantity: qty });
      toast.success(data.message);
      setReorderId(null);
      onChange();
      setTab("quotes");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reorder failed");
    } finally { setBusyId(null); }
  };
  if (orders === null) return <div className="text-slate-400 py-10">Loading your orders...</div>;
  if (orders.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center" data-testid="portal-orders-empty">
        <p className="text-[15px] text-slate-500">No orders yet. Once you accept a quote, your order and delivery tracking appear here.</p>
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="portal-order-list">
      {orders.map((o) => (
        <div key={o.id} className="bg-white rounded-2xl border border-slate-200 p-6" data-testid={`portal-order-${o.order_number}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <span className="font-[Poppins] font-bold text-[16px] text-[var(--pg-navy)]">{o.order_number}</span>
              <span className="ml-3 text-[13.5px] text-slate-500">{o.product} · Qty {o.quantity} · {money(o.total)}</span>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="text-[12.5px] text-slate-500">
                {o.carrier && <span className="mr-3">Carrier: <strong className="text-[var(--pg-navy)]">{o.carrier}</strong></span>}
                {o.tracking_number && <span className="mr-3">Tracking: <strong className="text-[var(--pg-navy)]">{o.tracking_number}</strong></span>}
                {o.estimated_delivery && <span>ETA: <strong className="text-[var(--pg-navy)]">{o.estimated_delivery}</strong></span>}
              </div>
              {o.status === "DELIVERED" && (
                <button data-testid={`portal-reorder-${o.id}`} onClick={() => (reorderId === o.id ? setReorderId(null) : openReorder(o))}
                  className="btn-primary !py-2 !px-4 text-[12.5px]">
                  <Repeat size={14} /> Reorder
                </button>
              )}
            </div>
          </div>
          {reorderId === o.id && (
            <div data-testid={`portal-reorder-form-${o.id}`} className="mt-4 flex flex-wrap items-center gap-2.5 bg-slate-50 rounded-xl p-4">
              <span className="text-[13px] text-slate-600 font-medium">Reorder {o.product} — quantity:</span>
              <input data-testid={`portal-reorder-qty-${o.id}`} className="pg-input !py-2 !w-[110px] text-[13px]"
                value={qty} onChange={(e) => setQty(e.target.value)} maxLength={20} />
              <button data-testid={`portal-reorder-confirm-${o.id}`} onClick={() => reorder(o)} disabled={busyId === o.id}
                className="btn-primary !py-2 !px-4 text-[12.5px] disabled:opacity-70">
                {busyId === o.id ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />} Confirm Reorder
              </button>
              <button data-testid={`portal-reorder-cancel-${o.id}`} onClick={() => setReorderId(null)}
                className="text-[12.5px] font-[Poppins] font-semibold text-slate-500 px-2">Cancel</button>
              <span className="w-full text-[11.5px] text-slate-400">We'll confirm current pricing and send you a fresh quote — fast-tracked.</span>
            </div>
          )}
          {o.status === "CANCELLED" ? (
            <div className="mt-4 inline-block px-3 py-1.5 rounded-full text-[12px] font-[Poppins] font-semibold bg-red-100 text-red-600">CANCELLED</div>
          ) : (
            <OrderTimeline status={o.status} timeline={o.timeline || []} />
          )}
        </div>
      ))}
    </div>
  );
}

function OrderTimeline({ status, timeline }) {
  const currentIdx = ORDER_STEPS.indexOf(status);
  const reached = Object.fromEntries((timeline || []).map((t) => [t.status, t.at]));
  return (
    <div className="mt-5 overflow-x-auto">
      <div className="flex items-start min-w-[680px]" data-testid="portal-order-timeline">
        {ORDER_STEPS.map((s, i) => {
          const done = i <= currentIdx;
          const hasEntry = Boolean(reached[s]);
          const solid = hasEntry || i === currentIdx;
          return (
            <div key={s} className="flex-1 flex flex-col items-center relative">
              {i > 0 && <div className={`absolute top-[13px] right-1/2 w-full h-[3px] ${i <= currentIdx ? "bg-[var(--pg-accent)]" : "bg-slate-200"}`} style={{ zIndex: 0 }}></div>}
              <div className={`relative z-10 w-7 h-7 rounded-full flex items-center justify-center ${done ? (solid ? "bg-[var(--pg-accent)] text-white" : "bg-[var(--pg-accent)]/30 text-white") : "bg-slate-200 text-slate-400"}`}>
                {done && solid ? <Check size={14} /> : done ? <span className="w-2 h-2 rounded-full bg-white"></span> : <span className="text-[11px] font-bold">{i + 1}</span>}
              </div>
              <div className={`mt-2 text-[10.5px] font-[Poppins] font-semibold uppercase tracking-wide text-center px-1 ${done ? "text-[var(--pg-navy)]" : "text-slate-400"}`}>
                {s.replace(/_/g, " ")}
              </div>
              {reached[s] && <div className="text-[10px] text-slate-400 mt-0.5">{reached[s].slice(0, 10)}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const INVOICE_BADGE = { SENT: "bg-blue-100 text-blue-700", PAID: "bg-green-100 text-green-700", OVERDUE: "bg-red-100 text-red-600", VOID: "bg-slate-200 text-slate-500" };

function InvoicesTab({ invoices }) {
  if (invoices === null) return <div className="text-slate-400 py-10">Loading your invoices...</div>;
  if (invoices.length === 0) {
    return <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center" data-testid="portal-invoices-empty"><p className="text-[15px] text-slate-500">No invoices yet. Invoices appear here once your orders are billed.</p></div>;
  }
  const pdf = async (inv) => {
    try {
      const res = await api.get(`/portal/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${inv.invoice_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF download failed"); }
  };
  const pay = async (inv) => {
    try {
      const r = await api.post(`/portal/invoices/${inv.id}/pay`, { origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch { toast.error("Could not start payment. Please try again."); }
  };
  return (
    <div className="space-y-3" data-testid="portal-invoice-list">
      {invoices.map((inv) => (
        <div key={inv.id} className="bg-white rounded-2xl border border-slate-200 p-6 flex flex-wrap items-center justify-between gap-3" data-testid={`portal-invoice-${inv.invoice_number}`}>
          <div>
            <span className="font-[Poppins] font-bold text-[16px] text-[var(--pg-navy)]">{inv.invoice_number}</span>
            <span className={`ml-3 px-2.5 py-1 rounded-full text-[11px] font-[Poppins] font-semibold ${INVOICE_BADGE[inv.status] || "bg-slate-100"}`}>{inv.status}</span>
            <div className="mt-1.5 text-[13px] text-slate-500">
              {inv.product} · Order {inv.order_number} · <strong className="text-[var(--pg-navy)]">{money(inv.total)}</strong>
              {inv.status !== "PAID" && <span className="ml-2 text-amber-600 font-medium">Due {inv.due_date} ({inv.payment_terms})</span>}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {(inv.status === "SENT" || inv.status === "OVERDUE") && (
              <button data-testid={`portal-invoice-pay-${inv.id}`} onClick={() => pay(inv)}
                className="btn-primary !py-2 !px-4 text-[12.5px]"><CreditCard size={14} /> Pay Now</button>
            )}
            <button data-testid={`portal-invoice-pdf-${inv.id}`} onClick={() => pdf(inv)}
              className="inline-flex items-center gap-1.5 text-[12.5px] font-[Poppins] font-semibold px-3.5 py-2 rounded-lg bg-[var(--pg-bg)] border border-slate-200 hover:border-[var(--pg-accent)]"><FileDown size={14} /> Download PDF</button>
            {inv.status !== "PAID" && <span data-testid="invoice-payment-note" className="text-[12.5px] font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1">Pay online by card, or by check/wire per terms</span>}
            {inv.status === "PAID" && (
              <span data-testid={`invoice-payment-history-${inv.id}`} className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-green-800 bg-green-50 border border-green-200 rounded-md px-2.5 py-1">
                <Check size={13} /> Paid {(inv.payment_details?.date || inv.paid_at) ? new Date(inv.payment_details?.date || inv.paid_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}
                {inv.payment_details?.card_brand && <> · {inv.payment_details.card_brand.toUpperCase()} •••• {inv.payment_details.card_last4}</>}
                {inv.payment_details?.receipt_url && (
                  <a data-testid={`invoice-receipt-link-${inv.id}`} href={inv.payment_details.receipt_url} target="_blank" rel="noreferrer" className="underline font-semibold hover:text-green-900">View receipt</a>
                )}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

const NTYPE_COLOR = { quote_sent: "bg-blue-500", order_status: "bg-emerald-500", invoice: "bg-amber-500", payment: "bg-green-600" };

function NotificationsBell({ setTab }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });

  const load = useCallback(() => {
    api.get("/portal/notifications").then((r) => setData(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && data.unread > 0) {
      api.post("/portal/notifications/mark-read")
        .then(() => setData((d) => ({ ...d, unread: 0 })))
        .catch(() => {});
    }
  };

  return (
    <div className="relative z-50">
      <button data-testid="portal-notifications-bell" onClick={toggle} aria-label="Notifications"
        className="relative z-50 inline-flex items-center justify-center w-[42px] h-[42px] rounded-full border border-white/30 text-white/80 hover:text-white transition-colors">
        <Bell size={17} />
        {data.unread > 0 && (
          <span data-testid="portal-notifications-badge"
            className="absolute -top-1 -right-1 min-w-[19px] h-[19px] px-1 rounded-full bg-red-500 text-white text-[10.5px] font-[Poppins] font-bold flex items-center justify-center">
            {data.unread > 9 ? "9+" : data.unread}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div data-testid="portal-notifications-panel"
            className="absolute right-0 top-[50px] z-50 w-[340px] max-h-[420px] overflow-y-auto bg-white rounded-2xl border border-slate-200 shadow-xl">
            <div className="px-4 py-3 border-b border-slate-100 font-[Poppins] font-bold text-[14px] text-[var(--pg-navy)]">Notifications</div>
            {data.items.length === 0 ? (
              <p data-testid="portal-notifications-empty" className="px-4 py-8 text-center text-[13px] text-slate-400">
                No notifications yet. Quote replies, order updates and invoices will appear here.
              </p>
            ) : data.items.map((n) => (
              <button key={n.id} data-testid={`portal-notification-${n.id}`}
                onClick={() => { if (n.tab) setTab(n.tab); setOpen(false); }}
                className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors ${n.read ? "" : "bg-blue-50/60"}`}>
                <div className="flex items-start gap-2.5">
                  <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${NTYPE_COLOR[n.type] || "bg-slate-400"}`} />
                  <div className="min-w-0">
                    <div className="text-[13px] font-[Poppins] font-semibold text-[var(--pg-navy)] leading-snug">{n.title}</div>
                    {n.message && <div className="mt-0.5 text-[12px] text-slate-500 leading-snug">{n.message}</div>}
                    <div className="mt-1 text-[11px] text-slate-400">{(n.created_at || "").slice(0, 10)}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
